import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  getQuarter,
  getYear,
  isValid,
  isSameDay,
  isSameYear,
  isToday,
  isTomorrow,
  isYesterday,
  parse,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';

const today = startOfDay(new Date());
export const rangeConfig = {
  'Last 1 Month': { startDate: subMonths(today, 1), endDate: today },
  'Last 3 Months': { startDate: subMonths(today, 3), endDate: today },
  'Last 6 Months': { startDate: subMonths(today, 6), endDate: today },
  'Last 1 Year': { startDate: subYears(today, 1), endDate: today },
  'Last 2 Years': { startDate: subYears(today, 2), endDate: today },
  'Previous Year': {
    startDate: startOfYear(subYears(today, 1)),
    endDate: endOfYear(subYears(today, 1)),
  },
  'Year To Date': { startDate: startOfYear(today), endDate: today },
  'Current Year': { startDate: startOfYear(today), endDate: endOfYear(today) },
  Default: { startDate: startOfYear(today), endDate: today },
};

export const parseArrayWithDatabaseDateToUnix = (arrayWithDate: { date: string }[]) => {
  return arrayWithDate.map((item) => {
    const unixTimestamp = new Date(item.date).getTime(); // Divide by 1000 to get Unix timestamp in seconds

    return {
      ...item,
      date: unixTimestamp,
    };
  });
};

/**
 * The function `convertFormatOfDateString` takes a date string in one format and
 * converts it to another format of date string.
 * @param dateString - The dateString parameter is a 'string' representing a date in
 * the format specified by the formatFrom parameter.
 * @param [formatFrom=MM/dd/yyyy] - The `formatFrom` parameter is the format of the
 * input `dateString`.
 * @param [formatTo=yyyy-MM-dd] - The `formatTo` parameter is the desired format of
 * the date string that you want to convert to.
 * @returns a formatted date of type 'string' based on the input date and the
 * specified formats.
 */
export function convertFormatOfDateString(
  dateString: string,
  formatFrom = 'MM/dd/yyyy',
  formatTo = 'yyyy-MM-dd',
): string {
  const parsedDate = parse(dateString, formatFrom, new Date());
  const formattedDate = format(parsedDate, formatTo);

  return formattedDate;
}

export function convertDateRangeToFriendlyName(
  startDateString: string,
  endDateString: string,
  dateFormat = 'MM/dd/yyyy',
): string {
  if (!startDateString || !endDateString) return 'No date range';

  const startDate = parse(startDateString, dateFormat, new Date());
  const endDate = parse(endDateString, dateFormat, new Date());

  if (isSameDay(startDate, endDate)) {
    if (isToday(startDate)) {
      return 'Today';
    }
    if (isYesterday(startDate)) {
      return 'Yesterday';
    }
    if (isTomorrow(startDate)) {
      return 'Tomorrow';
    }
    return format(startDate, 'dd MMM yyyy');
  }

  if (
    startOfDay(startOfMonth(startDate)).getTime() === startOfDay(startDate).getTime() &&
    startOfDay(endOfMonth(startDate)).getTime() === startOfDay(endDate).getTime()
  ) {
    return `${format(startDate, 'MMMM yyyy')}`;
  }

  if (
    startOfDay(startOfQuarter(startDate)).getTime() === startOfDay(startDate).getTime() &&
    startOfDay(endOfQuarter(startDate)).getTime() === startOfDay(endDate).getTime()
  ) {
    return `Q${getQuarter(startDate)} ${getYear(startDate)}`;
  }

  if (
    isSameYear(startDate, endDate) &&
    startOfDay(startOfYear(startDate)).getTime() === startOfDay(startDate).getTime() &&
    startOfDay(endOfYear(startDate)).getTime() === startOfDay(endDate).getTime()
  ) {
    return `FY ${getYear(startDate)}`;
  }

  return `${format(startDate, 'dd MMM yyyy')} - ${format(endDate, 'dd MMM yyyy')}`;
}

export function convertDateObjToDateString(dateObj: Date, formatString = 'MM/dd/yyyy'): string {
  return format(dateObj, formatString);
}

export function convertDateToDateObj(
  dateString: string | Date,
  formatString = 'EEE MMM dd yyyy',
): Date {
  if (!dateString) return new Date();
  if (dateString instanceof Date) return dateString;
  if (typeof dateString !== 'string') return new Date(NaN);

  const trimmedDate = dateString.trim();
  if (!trimmedDate) return new Date();

  const dateByIncomingFormat = parse(trimmedDate, formatString, new Date());
  if (isValid(dateByIncomingFormat)) return dateByIncomingFormat;

  const dateFormatsToTry = [
    'yyyy-MM-dd',
    'MM/dd/yyyy',
    'EEE MMM dd yyyy',
    "yyyy-MM-dd'T'HH:mm:ss",
    "yyyy-MM-dd'T'HH:mm:ss.SSS",
    "yyyy-MM-dd'T'HH:mm:ssXXX",
    'yyyy-MM-dd HH:mm:ss',
    'yyyy-MM-dd HH:mm:ss.SSS',
    'yyyy/MM/dd',
    'yyyy/MM/dd HH:mm:ss',
  ];

  for (const dateFormat of dateFormatsToTry) {
    const parsedDate = parse(trimmedDate, dateFormat, new Date());
    if (isValid(parsedDate)) return parsedDate;
  }

  const isoParsedDate = parseISO(trimmedDate);
  if (isValid(isoParsedDate)) return isoParsedDate;

  const nativeParsedDate = new Date(trimmedDate);
  if (isValid(nativeParsedDate)) return nativeParsedDate;

  return new Date(NaN);
}

export function getFilterDateRangeLabel(startDate: Date, endDate: Date): string {
  if (!startDate || !endDate) return '';
  if (endDate.getTime() !== rangeConfig['Previous Year'].endDate.getTime()) {
    endDate = startOfDay(endDate);
  }

  for (const [range, dates] of Object.entries(rangeConfig)) {
    const configStartDate = dates.startDate;
    const configEndDate = dates.endDate;

    if (isSameDay(startDate, configStartDate) && isSameDay(endDate, configEndDate)) {
      return range;
    }
  }

  return '';
}
